import { Localized } from "@fluent/react"
import {
    Alert,
    Box,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    TextField,
} from "@mui/material"
import { useEffect, useState } from "react"
import { useAppDispatch } from "../../../app/hooks"
import { CancelButton } from "../../../app/components/CancelButton"
import { SaveButton } from "../../../app/components/SaveButton"
import { closeStructureDefinition, StructureDefinitionDto } from "../structureSlice"

const RequiredError = () => (
    <Localized id="field-is-required">
        <span>_Pole jest wymagane</span>
    </Localized>
)

type CloseStructureDialogProps = {
    open?: boolean
    structure?: StructureDefinitionDto | null
    onClose?: () => void
    onClosed?: () => void
}

function formatDateForInput(date: Date): string {
    return date.toISOString().split("T")[0]
}

export const CloseStructureDialog = ({ open = true, structure, onClose, onClosed }: CloseStructureDialogProps) => {
    const dispatch = useAppDispatch()
    const [closedAt, setClosedAt] = useState(formatDateForInput(new Date()))
    const [validationErrors, setValidationErrors] = useState(false)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (open) {
            setClosedAt(formatDateForInput(new Date()))
            setValidationErrors(false)
        }
    }, [open])

    const closedAtError = validationErrors && closedAt.trim() === ""

    const handleSave = async () => {
        setValidationErrors(true)

        if (!structure || closedAt.trim() === "") {
            return
        }

        setSaving(true)
        try {
            const result = await dispatch(closeStructureDefinition({
                structureDefinitionId: structure.id,
                request: {
                    closedAt: new Date(closedAt),
                },
            }))

            if (closeStructureDefinition.fulfilled.match(result)) {
                if (onClosed) {
                    onClosed()
                }
                if (onClose) {
                    onClose()
                }
            } else {
                console.error(result.payload)
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitle>
                <Localized id="structures-close-title">
                    <span>_Zamknięcie struktury</span>
                </Localized>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ pt: 1, display: "grid", gap: 2 }}>
                    <Alert severity="warning">
                        <Localized id="structures-close-warning">
                            <span>_Uwaga: struktura zostanie zamknięta i nie będzie aktywna od wskazanej daty.</span>
                        </Localized>
                    </Alert>

                    <FormControl fullWidth error={closedAtError}>
                        <TextField
                            required
                            type="date"
                            value={closedAt}
                            onChange={(e) => setClosedAt(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            error={closedAtError}
                            label={
                                <Localized id="structures-close-date">
                                    <span>_Data zamknięcia</span>
                                </Localized>
                            }
                        />
                        {closedAtError && <FormHelperText><RequiredError /></FormHelperText>}
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <ButtonGroup variant="contained" color="secondary">
                    <SaveButton loading={saving} onClick={handleSave} />
                    <CancelButton loading={saving} onClick={() => onClose?.()} />
                </ButtonGroup>
            </DialogActions>
        </Dialog>
    )
}
